import type { RouteRecordRaw } from 'vue-router'

import masterPP from '~/scopes/product-core/middleware/masterPP/masterPP'
import prefetchAndValidate from '~/scopes/product-core/middleware/prefetchAndValidate/prefetchAndValidate'
import redirectInvalidUUID from '~/scopes/product-core/middleware/redirectInvalidUUID/redirectInvalidUUID'
import jpProductRoutes from '~/scopes/product-core/pages/ProductDetailPage/jp/routes.jp'
import { PRODUCT_ROUTES } from '~/scopes/product-core/route-names'
import { OBSOLETE_DRAWER_TYPES } from '~/scopes/product-core/routes.constants'
import { validateMarketRoutes } from '~/scopes/product-core/routes.validate'

export default (): RouteRecordRaw[] => {
  const jpRoutes = jpProductRoutes()

  const globalRoutes: RouteRecordRaw[] = [
    {
      component: () =>
        import('~/scopes/product-core/pages/ProductProxy/ProductProxy.vue'),
      name: PRODUCT_ROUTES.VARIANT_PRODUCT_PAGE,
      path: 'p/:slugV2/:uuid',
      meta: {
        middleware: [redirectInvalidUUID, prefetchAndValidate],
        owners: ['bot-squad-product-page-front'],
        cache: 'public, max-age=90000, stale-while-revalidate=90000',
        tracking: { contentGroup: 'Product Detail Page' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/pages/ProductProxy/ProductProxy.vue'),
      name: PRODUCT_ROUTES.MASTER_PRODUCT_PAGE,
      path: 'p/:slug',
      meta: {
        middleware: [masterPP, prefetchAndValidate],
        owners: ['bot-squad-product-page-front'],
        cache: 'public, max-age=90000, stale-while-revalidate=90000',
        tracking: { contentGroup: 'Product Detail Page' },
      },
    },
    // Webviews
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/CustomizationFunnel/components/VisibleByVerizonDrawer/components/VisibleByVerizonDrawerBody/VisibleByVerizonDrawerBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_VISIBLE_BY_VERIZON_DRAWER,
      path: 'p/drawers/visible-by-verizon',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ProcessorModal/components/MacbookProcessorModal/components/MacbookProcessorModalBody/MacbookProcessorModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_MACBOOK_PROCESSOR_DRAWER,
      path: 'p/drawers/processor/macbook',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ProcessorModal/components/LaptopProcessorModal/components/LaptopProcessorModalBody/LaptopProcessorModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_LAPTOP_PROCESSOR_DRAWER,
      path: 'p/drawers/processor/laptop',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/ReassuranceItems/components/ItemObsolete/components/ItemObsoleteSoftwareModal/components/ItemObsoleteSoftwareModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_OBSOLETE_SOFTWARE_DRAWER,
      path: `p/drawers/${OBSOLETE_DRAWER_TYPES[PRODUCT_ROUTES.WEBVIEWS_OBSOLETE_SOFTWARE_DRAWER]}`,
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/ReassuranceItems/components/ItemObsolete/components/ItemObsoleteOSModal/components/ItemObsoleteOSModalBody/ObsoleteOSModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_OBSOLETE_OS_DRAWER,
      path: `p/drawers/${OBSOLETE_DRAWER_TYPES[PRODUCT_ROUTES.WEBVIEWS_OBSOLETE_OS_DRAWER]}`,
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/Seller/components/SellerDetailsModal/components/SellerDetailsModalBody/SellerDetailsModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_SELLER_DETAILS_DRAWER,
      path: 'p/drawers/seller-details',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/containers/ContainerSellerBlock/components/OtherSellersBlock/components/OtherSellersDrawer/components/OtherSellersDrawerBody/OtherSellersDrawerBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_OTHER_SELLERS_DRAWER,
      path: 'p/drawers/other-sellers/:productId',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/CustomizationFunnel/components/SimModal/components/SimModalBody/SimModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_SIM_DRAWER,
      path: 'p/drawers/sim',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/CustomizationFunnel/components/NewBatteryModal/components/BatteryDrawerBody/BatteryDrawerBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_BATTERY_DRAWER,
      path: 'p/drawers/battery/:categoryId?',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    /**
     * @deprecated Use WEBVIEWS_MACBOOK_PROCESSOR_DRAWER instead
     */
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ProcessorModal/components/MacbookProcessorModal/components/MacbookProcessorModalBody/MacbookProcessorModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_PROCESSOR_DRAWER,
      path: 'p/drawers/processor',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    /**
     * @deprecated Use WEBVIEWS_MACBOOK_PROCESSOR_DRAWER instead
     */
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/ReassuranceItems/components/ItemObsolete/components/ItemObsoleteOSModal/components/ItemObsoleteOSModalBody/ObsoleteOSModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_OBSOLETE_DRAWER,
      path: 'p/drawers/obsolete',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
  ]

  // Dev-only: fails loudly if JP routes ever override or shadow global ones.
  // No-op in production (stripped by the bundler). See `routes.validate.ts`.
  validateMarketRoutes(jpRoutes, globalRoutes, {
    market: 'JP',
    namePrefix: 'jp_',
    owner: 'bot-squad-apac-front',
  })

  return [...globalRoutes, ...jpRoutes]
}
